import JSZip from "jszip";

export async function loadZipPackage(file) {
  const zip = await JSZip.loadAsync(file);
  const manifestFile = zip.file("imsmanifest.xml");
  if (!manifestFile) {
    throw new Error("Invalid Brightspace package: imsmanifest.xml not found.");
  }
  const xmlString = await manifestFile.async("text");
  const parser = new DOMParser();
  const manifestDoc = parser.parseFromString(xmlString, "application/xml");
  
  const pages = [];
  const items = Array.from(manifestDoc.querySelectorAll("item"));
  for (const item of items) {
    const ref = item.getAttribute("identifierref");
    if (!ref) continue;
    const resource = manifestDoc.querySelector(`resource[identifier="${ref}"]`);
    if (!resource) continue;
    const href = resource.getAttribute("href");
    if (href && href.toLowerCase().includes("course-pages") && href.toLowerCase().endsWith(".html")) {
      pages.push({
        itemId: item.getAttribute("identifier"),
        itemTitle: item.querySelector("title")?.textContent || "",
        resourceId: ref,
        href: href,
        fileName: href.split(/[/\\]/).pop(),
        itemNode: item,
        resourceNode: resource
      });
    }
  }

  return {
    zipInstance: zip,
    manifestDoc: manifestDoc,
    pages: pages
  };
}

export async function updateManifestAndZip(zipInstance, manifestDoc, convertedFiles, extractedImages = [], customCssPath = "") {
  const pages = [];
  const items = Array.from(manifestDoc.querySelectorAll("item"));
  for (const item of items) {
    const ref = item.getAttribute("identifierref");
    if (!ref) continue;
    const resource = manifestDoc.querySelector(`resource[identifier="${ref}"]`);
    if (!resource) continue;
    const href = resource.getAttribute("href");
    if (href && href.toLowerCase().includes("course-pages") && href.toLowerCase().endsWith(".html")) {
      pages.push({
        itemNode: item,
        resourceNode: resource,
        href: href
      });
    }
  }

  Object.keys(zipInstance.files).forEach(relativePath => {
    const normalized = relativePath.replace(/\\/g, "/").toLowerCase();
    if (normalized.startsWith("course-pages/") && (normalized.endsWith(".html") || normalized.endsWith(".htm"))) {
      zipInstance.remove(relativePath);
    }
  });

  const limit = Math.min(convertedFiles.length, pages.length);
  for (let i = 0; i < limit; i++) {
    const file = convertedFiles[i];
    const page = pages[i];
    
    const titleMatch = file.content.match(/<title>([^<]+)<\/title>/i);
    const pageTitle = titleMatch ? titleMatch[1] : file.name.replace(/\.html$/i, "");

    const titleNode = page.itemNode.querySelector("title");
    if (titleNode) {
      titleNode.textContent = pageTitle;
    }

    const zipNewPath = `course-pages/${file.name}`;
    zipInstance.file(zipNewPath, file.content);

    page.resourceNode.setAttribute("href", `course-pages\\${file.name}`);
  }

  if (convertedFiles.length > pages.length) {
    const resourcesParent = manifestDoc.querySelector("resources");
    const lastPage = pages[pages.length - 1];
    const organizationNode = lastPage ? lastPage.itemNode.parentNode : manifestDoc.querySelector("organization");

    for (let i = pages.length; i < convertedFiles.length; i++) {
      const file = convertedFiles[i];
      const titleMatch = file.content.match(/<title>([^<]+)<\/title>/i);
      const pageTitle = titleMatch ? titleMatch[1] : file.name.replace(/\.html$/i, "");

      const resId = `RES_CONTENT_NEW_${i}`;
      const itemId = `item_new_${i}`;

      const newResource = manifestDoc.createElement("resource");
      newResource.setAttribute("identifier", resId);
      newResource.setAttribute("type", "webcontent");
      newResource.setAttribute("href", `course-pages\\${file.name}`);
      resourcesParent.appendChild(newResource);

      const newItem = manifestDoc.createElement("item");
      newItem.setAttribute("identifier", itemId);
      newItem.setAttribute("identifierref", resId);
      const newTitle = manifestDoc.createElement("title");
      newTitle.textContent = pageTitle;
      newItem.appendChild(newTitle);
      organizationNode.appendChild(newItem);

      zipInstance.file(`course-pages/${file.name}`, file.content);
    }
  } else if (pages.length > convertedFiles.length) {
    for (let j = convertedFiles.length; j < pages.length; j++) {
      const page = pages[j];
      const zipOldPath = page.href.replace(/\\/g, "/");
      zipInstance.remove(zipOldPath);
      if (page.itemNode.parentNode) {
        page.itemNode.parentNode.removeChild(page.itemNode);
      }
      if (page.resourceNode.parentNode) {
        page.resourceNode.parentNode.removeChild(page.resourceNode);
      }
    }
  }

  for (const img of extractedImages) {
    const binaryData = atob(img.data);
    const array = new Uint8Array(binaryData.length);
    for (let k = 0; k < binaryData.length; k++) {
      array[k] = binaryData.charCodeAt(k);
    }
    zipInstance.file(`images/${img.name}`, array);
  }

  if (customCssPath && customCssPath.startsWith("./ZZ_technical-files/")) {
    const cssZipPath = customCssPath.replace(/^\.\//, "");
    if (!zipInstance.file(cssZipPath)) {
      zipInstance.file(cssZipPath, "");
    }
  }

  const serializer = new XMLSerializer();
  const updatedXml = serializer.serializeToString(manifestDoc);
  zipInstance.file("imsmanifest.xml", updatedXml);

  const blob = await zipInstance.generateAsync({ type: "blob" });
  return blob;
}
